<template>
  <div
    class="date-range-picker"
    :class="{
      'date-range-picker--open': isOpen,
    }"
  >
    <label v-if="label">{{ label }}</label>
    <div class="date-range-picker_wrapper">
      <date-range-picker
        ref="picker"
        v-model="selectedDates"
        single-date-picker="range"
        control-container-class="container"
        :date-range="value"
        :locale-data="localeData"
        :min-date="minDate"
        :max-date="maxDate"
        :time-picker="false"
        :time-picker24-hour="false"
        :show-week-numbers="false"
        :show-dropdowns="true"
        :date-format="dateFormat"
        :ranges="false"
        :auto-apply="true"
        :append-to-body="true"
        :enable-time-picker="false"
        locale="fr"
        format="dd/MM/yyyy"
        :range="true"
        :clearable="false"
        :disabled-week-days="disableDays"
        @toggle="onToggle"
        @select="onSelect"
      >
        <template #input="picker">
          <span v-if="!picker.startDate && !picker.endDate" class="placeholder">
            <slot name="placeholder" />
          </span>
          <span v-if="picker.startDate || picker.endDate" class="filled">{{ getText(picker) }}</span>
          <span class="adn-icon adn-icon--function-calendar" aria-hidden="true" />
        </template>
      </date-range-picker>
      <span class="adn-icon adn-icon--status-deleted" :title="resetTitle" @click="onReset" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import DateRangePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default defineComponent({
  name: 'PDateRangePicker',
  components: {
    DateRangePicker,
  },
  props: {
    label: {
      type: String,
    },
    minDate: {
      type: [Date, String],
      default: null,
    },
    maxDate: {
      type: [Date, String],
      default: null,
    },
    value: {
      type: Object,
      default: () => ({ startDate: undefined, endDate: undefined }),
    },
    modelValue: {
      type: Object,
      default: () => ({ startDate: undefined, endDate: undefined }),
    },
    resetTitle: {
      type: String,
      default: 'Réinitialiser la date de livraison',
    },
  },
  setup(props, { emit }) {
    const selectedDates = ref([
      props.modelValue?.startDate || props.value?.startDate,
      props.modelValue?.startDate || props.value?.endDate,
    ])
    const isOpen = ref(false)
    const picker = ref<any>(null)
    const localeData = {
      direction: 'ltr',
      format: 'dd/mm/yyyy',
      separator: ' au ',
      applyLabel: 'Valider',
      cancelLabel: 'Annuler',
      daysOfWeek: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      monthNames: ['Jan', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juill', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
      firstDay: 1,
    }
    const SATURDAY = 6
    const SUNDAY = 0
    const disableDays = ref([SATURDAY, SUNDAY])

    const dateFormat = (classes: any, date: Date) => {
      const newClasses = classes
      if (!classes.disabled) {
        newClasses.disabled = disableDays.value.includes(date.getDay())
      }
      return newClasses
    }
    const areDatesEqual = (startDate: Date, endDate: Date) => {
      return startDate.valueOf() === endDate.valueOf()
    }
    const getText = (value: any): string => {
      if (areDatesEqual(value.startDate, value.endDate)) {
        return value.rangeText.split(localeData.separator)[0]
      }
      return value.rangeText
    }
    const onToggle = (open: boolean) => {
      isOpen.value = open
    }
    function formatDate(date: Date) {
      const day = date.getDate()
      const month = date.getMonth() + 1
      const year = date.getFullYear()

      const formattedDay = day < 10 ? `0${day}` : day
      const formattedMonth = month < 10 ? `0${month}` : month

      return `${formattedDay}/${formattedMonth}/${year}`
    }

    watch(
      () => selectedDates.value,
      (newValue) => {
        if (!newValue) emit('update:modelValue', { startDate: null, endDate: null })
        else emit('update:modelValue', { startDate: formatDate(newValue[0]), endDate: formatDate(newValue[1]) })
      },
    )

    const onSelect = ({ startDate, endDate }) => {
      emit('input', { startDate, endDate })
    }
    const onReset = () => {
      if (picker.value?.clearValue) picker.value?.clearValue()

      emit('input', { startDate: null, endDate: null })
    }
    return {
      dateFormat,
      getText,
      isOpen,
      localeData,
      onReset,
      onSelect,
      onToggle,
      selectedDates,
      picker,
      disableDays,
    }
  },
})
</script>

<style lang="scss">
:root {
  --dp-font-size: 1.4rem;
}

.date-range-picker {
  > * {
    display: block;
    margin-bottom: var(--spacing-1);
  }

  &_wrapper {
    display: flex;

    .vue-daterange-picker {
      width: 28rem;
    }

    > .adn-icon {
      cursor: pointer;
      margin-top: var(--spacing-1);
    }
  }

  .container {
    border: 1px solid var(--color-text);
    border-radius: var(--border-radius-1);
    cursor: text;
    display: flex;
    padding: var(--spacing-1);
    user-select: none;

    &:hover {
      box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
    }
  }

  label {
    color: var(--color-text-light);
  }

  span {
    display: block;
    flex: 1;
    text-align: center;
  }

  .placeholder {
    color: var(--color-text-light);
  }

  .value {
    color: var(--color-text);
  }

  .adn-icon {
    flex: 0;
    margin-left: var(--spacing-1);
  }

  &--open {
    .container {
      box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
    }

    .filled {
      color: var(--color-text-light);
    }
  }
}
</style>
